import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import ReactPhoneInput from "react-phone-input-2";
import Notify from "../../../Common/Notify";
import PUBNUB from "pubnub";
import Parse from "parse";
import config from "../../../../config";
import { parseAction } from "../../../../actions/REST";
import { ToastContainer } from "react-toastify";
import {
  registerQueue,
  getQueue,
} from "../../../../actions/PatientAction";
import Logo from "../../../../assets/images/logo-blue.png";
import "./PatientQueue.css";

const PatientQueue = ({
  qrCode = "",
  pub = "",
  sub = "",
  ticket = "",
  queue = "",
  patient = "",
  contact = "",
  history = "",
}) => {
  const dispatch = useDispatch();

  const [code, setCode] = useState("");
  const [tick, setTicket] = useState("");
  const [queueCode, setQueueCode] = useState(0);
  const [currentServing, setCurrentServing] = useState(null);
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [patientQueue, setPatientQueue] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [internetConnection, setInternetConnection] = useState(true);

  const pubnubRef = React.useRef(null);
  const channelsRef = React.useRef([]);
  const listenerRef = React.useRef(null);

  const currentQrCodeRef = useRef(qrCode);
  useEffect(() => {
    currentQrCodeRef.current = qrCode;
    // Notify.success(" - TESTING 2 - ");
  }, [qrCode]);

  useEffect(() => {
    if (qrCode !== "") {
      setCode(qrCode);
      // fetchPubNubKey(qrCode);
      const keys = {
        publishKey: pub,
        subscribeKey: sub,
      }
      initializePubNub(keys, qrCode);
    } else {
      const route = "/u";
      history.push(route);
    }
  }, [qrCode]);

  useEffect(() => {
    if (ticket === "") return;

    setTicket(ticket);
    handleGetQueue(ticket, qrCode);
  }, [ticket]);
  
  useEffect(() => {
    if (queue === "") return;

    setPatientQueue(queue);
  }, [queue]);

  useEffect(() => {
    const onVisible = () => {
      // Check if we are back on the screen and PubNub exists
      if (document.visibilityState === "visible" && pubnubRef.current) {
        // Notify.info("Resuming connection...");
        
        // 1. Recover any missed messages while the phone was locked
        pubnubRef.current.reconnect(); 
        
        // 2. Re-subscribe to the specific channel stored in the ref
        if (channelsRef.current.length > 0) {
          pubnubRef.current.subscribe({
            channels: channelsRef.current,
          });
        }
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => {
    window.addEventListener("focus", () => {
      if (pubnubRef.current) {
        pubnubRef.current.reconnect();
        // Notify.info("Refreshing queue...");
      }
    });
  }, []);

  useEffect(() => {
    const handleOnlineStatus = () => {
      if (navigator.onLine) {
        Notify.success("Internet Restored");
        // Add PubNub reconnect logic here if needed
        setInternetConnection(true);
        if (pubnubRef.current) pubnubRef.current.reconnect();
      } else {
        Notify.error("Internet Connection Lost");
        setInternetConnection(false);
      }
    };

    // Add listeners when component mounts
    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOnlineStatus);

    // CLEANUP: This replaces componentWillUnmount
    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOnlineStatus);
    };
  }, []);

  const fetchPubNubKey = (qrCode) => {
    parseAction("post", config.BASE_URL + "/parse/functions/getPubNubKeys")
      .then((keys) => {
        initializePubNub(keys.result, qrCode);
      })
      .catch((error) => {
        console.log("error", error);
      });
  };

  const initializePubNub = (keys, initialQrCode) => {
    if (pubnubRef.current) return;

    try {
      // Notify.info("PubNub: Initializing...");

      const guestId = "guest-" + Math.random().toString(36).substring(7);

      pubnubRef.current = new PUBNUB({
        publish_key: keys.publishKey,
        subscribe_key: keys.subscribeKey,
        uuid: guestId,
        ssl: true, // Force true
        origin: "pubsub.pubnub.com",
        heartbeatInterval: 10,
        keepAlive: true,
        provider: 'xhr',
        restore: true,
        autoNetworkDetection: true, // Handle switching between Wi-Fi and Data
      });

      const channelName = `queue_patient_channel_${initialQrCode}`;
      channelsRef.current = [channelName];

      const listener = {
        status: (s) => {
          // This will tell us if the mobile browser is blocking the socket
          // Notify.info(`Status: ${s.category}`); // tempt

          if (s.category === "PNConnectedCategory") {
            // Notify.success("CONNECTED!");
            setIsConnected(true);
          } else if (s.category === "PNNetworkIssuesCategory" || s.category === "PNNetworkDownCategory") {
            setIsConnected(false);
            // Notify.error("Connection lost. Trying to reconnect...");
          }
        },
        message: (m) => {
          // Notify.info("MSG RECEIVED");
          setCurrentServing(m.message.current_serving);
        },
      };

      listenerRef.current = listener;
      pubnubRef.current.addListener(listenerRef.current);
      
      // Trigger the subscription
      pubnubRef.current.subscribe({ 
        channels: channelsRef.current,
        withPresence: true // Sometimes helps trigger the handshake on mobile
      });
      
      // Notify.info("Subscribe Called...");

    } catch (err) {
      // If there is a syntax error or null reference, this will show on your mobile
      Notify.error("Init Crash: " + err.message);
    }
  };

  useEffect(() => {
    return () => {
      if (pubnubRef.current) {
        pubnubRef.current.unsubscribe({ channels: channelsRef.current });
        pubnubRef.current.removeListener(listenerRef.current);
      }
    };
  }, []);

  const handleRegisterQueue = () => {
    if (name !== "" && phoneNumber !== "") {
      const info = {
        name: name,
        mobile_number: phoneNumber,
        code: code,
      }
      dispatch(registerQueue(info))
        .then((res) => {
          const route = 
            "/patient-queue/" + code +
            "/" + pub +
            "/" + sub +
            "/"+ res.registration.ticket_id +
            "/" + res.ticket_number +
            "/" + name +
            "/" + phoneNumber;
          history.push(route);
        })
    } else {
      Notify.info("Name and Phone number required.");
    }
  }

  const handleGetQueue = (id, queuing_code) => {
    const params = {
      queue_code: queuing_code,
      ticket_id: id,
    };
    dispatch(getQueue(params))
      .then((res) => {
        setCurrentServing(res.current_serving);
      })
  }

  return (
    <div className="PatientQueue-container">
      <div className="PatientQueue-form">
        {tick === "" &&
          <React.Fragment>
            <h4 className="hc-primary-text">PATIENT WAITING FORM</h4>
            <div className="PatientQueue-form-registration Form-Container form-bypass">
              <>
                <label for="" className="label-cls">
                  Patient Name:
                </label>
                <div className="form-group">
                  <div className="card">
                    <input
                      id="name"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      style={{ textTransform: "uppercase" }}
                      type="text"
                      placeholder="Name"
                      required
                    />
                    <i className="fas fa-user hc-primary-text"></i>
                  </div>
                </div>
                <label for="" className="label-cls">
                  Patient Number:
                </label>
                <div className="form-group">
                  <div className="card" style={{ paddingLeft: "0px" }}>
                    <ReactPhoneInput
                      country={"ph"}
                      defaultCountry={"ph"}
                      enableLongNumbers={true}
                      onChange={(e) => setPhoneNumber(e)}
                      value={phoneNumber}
                      inputStyle={{
                        width: "100%",
                        outline: "none",
                        boxShadow: "none",
                      }}
                    />
                  </div>
                </div>
                <button
                  className="btn btn-hubchart btn-hubchart-close"
                  onClick={() => handleRegisterQueue()}
                >
                  Submit
                </button>
              </>
            </div>
          </React.Fragment>
        }
        {tick !== "" &&
        <React.Fragment>
          {!internetConnection && 
            <div className="PatientQueue-offline-container">
              <span>
                <i className="fas fa-wifi-slash"></i>   No internet connection
              </span>
            </div>
          }
          <div className="PatientQueue-form-main shadow-sm" style={{ margin: "10px 0px"}}>
            <span>
              <div style={{ fontWeight: "bold" }}>CURRENT NUMBER</div>
            </span>
            <span>
              {currentServing}
            </span>
          </div>
          <div className="PatientQueue-form-sub shadow-sm">
            {patientQueue === "" ?
              <></>
              :
              <div className="Patient-form-waiting">
                <span>Your</span>
                <span>Patient Number</span>
                <div>
                  {patientQueue}
                </div>
                {patient !== "" && contact !== "" ?
                  <>
                    <span></span>
                    <span style={{ textTransform: "uppercase" }}>
                      {patient}
                    </span>
                    <span>
                      {contact}
                    </span>
                  </>
                  : <></>
                }
              </div>
            }
          </div>
        </React.Fragment>
        }
        <img className="Logo" src={Logo} alt="Hubchart Logo" />
        <h4 className="hc-primary-text">HUBCHART</h4>
      </div>
      <ToastContainer />
    </div>
  );
};

export default PatientQueue;
